import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { grayFontColor } from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import { findBranchIndex, rulesHold } from "./builderConstants";
import useFormImage from "./useFormImage";

const WA_GREEN = "#008069";
const small = { fontSize: 12.5 };

const PreviewImage = ({ formId, component }) => {
  const url = useFormImage(formId, component.image_id);
  if (url) {
    return (
      <Box
        component="img"
        src={url}
        alt={component.alt || ""}
        sx={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: "8px" }}
      />
    );
  }
  return (
    <Box
      sx={{
        height: 110,
        borderRadius: "8px",
        display: "grid",
        placeItems: "center",
        fontSize: 11,
        color: grayFontColor,
        background:
          "repeating-linear-gradient(45deg,#eef0f4,#eef0f4 10px,#e5e8ee 10px,#e5e8ee 20px)",
      }}
    >
      {component.alt || "Image"}
    </Box>
  );
};

// `value`/`onAnswer` hold the answers that "Show only when" conditions read.
const PreviewComponent = ({ component, formId, interactive, value, onAnswer }) => {
  const required = component.required ? " *" : "";
  const label = `${component.label || ""}${required}`;
  const disabled = !interactive;
  const answer = { value: value ?? "", onChange: (e) => onAnswer(e.target.value) };

  switch (component.type) {
    case "heading":
      return <Typography sx={{ fontSize: 19, fontWeight: 600, lineHeight: 1.3 }}>{component.text}</Typography>;
    case "subheading":
      return <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{component.text}</Typography>;
    case "body":
      return <Typography sx={small}>{component.text}</Typography>;
    case "caption":
      return <Typography sx={{ fontSize: 11, color: grayFontColor }}>{component.text}</Typography>;
    case "image":
      return <PreviewImage formId={formId} component={component} />;
    case "link":
      return (
        <Typography sx={{ ...small, color: WA_GREEN, fontWeight: 500 }}>{component.text}</Typography>
      );
    case "short_answer":
      return (
        <TextField
          size="small"
          fullWidth
          label={label}
          disabled={disabled}
          {...answer}
          helperText={component.helper_text || undefined}
          type={component.input_type === "password" ? "password" : "text"}
          InputProps={{ sx: small }}
          InputLabelProps={{ sx: small }}
        />
      );
    case "paragraph":
      return (
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={3}
          label={label}
          disabled={disabled}
          {...answer}
          helperText={component.helper_text || undefined}
          InputProps={{ sx: small }}
          InputLabelProps={{ sx: small }}
        />
      );
    case "date":
      return (
        <TextField
          size="small"
          fullWidth
          type="date"
          label={label}
          disabled={disabled}
          helperText={component.helper_text || undefined}
          InputProps={{ sx: small }}
          InputLabelProps={{ sx: small, shrink: true }}
        />
      );
    case "single_choice":
    case "multiple_choice": {
      const Control = component.type === "single_choice" ? Radio : Checkbox;
      return (
        <Box>
          <Typography sx={{ ...small, color: grayFontColor, mb: 0.5 }}>{label}</Typography>
          {(component.options || []).map((option) => (
            <Box key={option.id} sx={{ borderBottom: "1px solid #f0f1f4" }}>
              <FormControlLabel
                labelPlacement="start"
                disabled={disabled}
                sx={{ width: "100%", justifyContent: "space-between", m: 0 }}
                control={
                  <Control
                    size="small"
                    sx={{ "&.Mui-checked": { color: WA_GREEN } }}
                    {...(component.type === "single_choice" && {
                      checked: value === option.id,
                      onChange: () => onAnswer(option.id),
                    })}
                  />
                }
                label={<Typography sx={small}>{option.title}</Typography>}
              />
            </Box>
          ))}
        </Box>
      );
    }
    case "dropdown":
      return (
        <TextField
          size="small"
          fullWidth
          select
          label={label}
          disabled={disabled}
          {...answer}
          InputProps={{ sx: small }}
          InputLabelProps={{ sx: small }}
        >
          <MenuItem value="" sx={small}>
            Select
          </MenuItem>
          {(component.options || []).map((option) => (
            <MenuItem key={option.id} value={option.id} sx={small}>
              {option.title}
            </MenuItem>
          ))}
        </TextField>
      );
    case "opt_in":
      return (
        <FormControlLabel
          disabled={disabled}
          control={<Checkbox size="small" sx={{ "&.Mui-checked": { color: WA_GREEN } }} />}
          label={<Typography sx={small}>{`${component.label || ""}${required}`}</Typography>}
        />
      );
    default:
      return null;
  }
};

/**
 * Phone mock-up of one screen of the builder model. When interactive, the footer
 * button moves through the screens like the real form.
 */
const PhonePreview = ({ model, screenIndex, interactive, formId, onScreenChange }) => {
  const screens = model?.screens || [];
  const screen = screens[Math.min(screenIndex, screens.length - 1)];
  const [toast, setToast] = useState(false);
  // { component id: answer } while trying the form, for "Show only when".
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => {
      setToast(false);
      setAnswers({});
      onScreenChange(0);
    }, 1600);
    return () => clearTimeout(timer);
  }, [toast, onScreenChange]);

  if (!screen) return null;
  // Screens after a conditional next screen are its targets; each ends the form.
  const branchIndex = findBranchIndex(model);
  const isLast =
    screenIndex >= screens.length - 1 || (branchIndex >= 0 && screenIndex > branchIndex);
  const branch = screen.footer?.branch?.component_id ? screen.footer.branch : null;

  const handleFooter = () => {
    if (!interactive) return;
    if (isLast) setToast(true);
    else if (branch) {
      const targetId = branch.targets?.[answers[branch.component_id]];
      const target = screens.findIndex((s) => s.id && s.id === targetId);
      if (target >= 0) onScreenChange(target); // no answer yet: stay, as on WhatsApp
    } else onScreenChange(screenIndex + 1);
  };

  return (
    <Box
      sx={{
        width: 300,
        maxWidth: "100%",
        height: 590,
        mx: "auto",
        border: "10px solid #1c2333",
        borderRadius: "34px",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#ece5dd",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10.5,
          px: 2,
          py: 0.75,
          position: "relative",
          zIndex: 2,
        }}
      >
        <span>9:41</span>
        <span>▮▮▮ ◔</span>
      </Box>
      <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,.35)" }} />
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          top: 46,
          backgroundColor: "#fff",
          borderRadius: "14px 14px 0 0",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 1.75,
            py: 1.5,
            borderBottom: "1px solid #e2e5ec",
            fontSize: 13,
          }}
        >
          <span>✕</span>
          <Box component="b" sx={{ fontWeight: 600, px: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {screen.title}
          </Box>
          <span>⋮</span>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", px: 2, py: 1.75, display: "flex", flexDirection: "column", gap: 1.75 }}>
          {/* While editing every component shows; trying the form hides the ones
              whose "Show only when" conditions don't match, as on WhatsApp. */}
          {(screen.components || [])
            .filter((component) => !interactive || rulesHold(component.visible_when, answers))
            .map((component) => (
              <PreviewComponent
                key={component.id}
                component={component}
                formId={formId}
                interactive={interactive}
                value={answers[component.id]}
                onAnswer={(value) =>
                  setAnswers((prev) => ({ ...prev, [component.id]: value }))
                }
              />
            ))}
        </Box>
        <Box sx={{ px: 1.75, pt: 1.25, pb: 1.5, borderTop: "1px solid #e2e5ec" }}>
          <Box
            component="button"
            type="button"
            onClick={handleFooter}
            sx={{
              ...typography.bodyLarge,
              width: "100%",
              border: 0,
              borderRadius: "20px",
              backgroundColor: WA_GREEN,
              color: "#fff",
              py: 1.25,
              cursor: interactive ? "pointer" : "default",
            }}
          >
            {screen.footer?.label || (isLast ? "Submit" : "Continue")}
          </Box>
          <Typography sx={{ fontSize: 10.5, textAlign: "center", color: grayFontColor, mt: 1 }}>
            Managed by the business. <Box component="span" sx={{ color: WA_GREEN }}>Learn more</Box>
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: 14,
            right: 14,
            bottom: 86,
            backgroundColor: "#1c2333",
            color: "#fff",
            fontSize: 11.5,
            px: 1.5,
            py: 1.1,
            borderRadius: "6px",
            opacity: toast ? 1 : 0,
            transition: "opacity .2s",
            pointerEvents: "none",
          }}
        >
          Form submitted · answers sent to BotBridge
        </Box>
      </Box>
    </Box>
  );
};

export default PhonePreview;
